import React from "react";
import {
  AreaChart,
  BarList,
  Card,
  FunnelSteps,
  Pill,
  SectionHead,
  formatNumber,
  formatPercent,
  formatRate,
  humanLabel,
  readDate,
} from "../../components/analytics/primitives";

/**
 * The dashboard's four content sections, in the order a parent moves through
 * the product: arrive → make something → what they made it with → come back.
 *
 * Each section takes the raw arrays from one API endpoint and reshapes them for
 * display. The reshaping lives here rather than in the API because it is
 * presentation logic — which rows to merge, how to rank, what to label.
 */

/* ------------------------------------------------------------- reshaping */

/**
 * Orders funnel steps.
 *
 * Deliberately does NOT sum: the API returns one row per step, already counted
 * distinct across the whole selected period. Summing per-day rows — as this
 * once did — counts a parent once for every day they were active, which
 * inflates each step and flatters the drop-off between them.
 */
const orderSteps = (rows) =>
  [...(rows || [])].sort((a, b) => a.step_order - b.step_order);

/**
 * The activation funnel arrives per sign-up cohort, so its rows do need
 * summing. Cohorts are disjoint sets of people — nobody signs up twice — so
 * adding them does not double-count anyone.
 */
const foldActivationSteps = (rows) =>
  Object.values(
    (rows || []).reduce((acc, row) => {
      const key = row.step_order;
      if (!acc[key]) acc[key] = { step_order: key, step_name: row.step_name, users: 0 };
      acc[key].users += row.users ?? 0;
      return acc;
    }, {})
  ).sort((a, b) => a.step_order - b.step_order);

/**
 * Sums a numeric field per category, returning ranked {label, value} items.
 * `raw` is kept alongside the readable label so callers can still filter on the
 * underlying value.
 */
const foldBy = (rows, labelKey, valueKey, fallback = "unknown") =>
  Object.values(
    (rows || []).reduce((acc, row) => {
      const key = row[labelKey] || fallback;
      if (!acc[key]) acc[key] = { raw: key, label: humanLabel(key), value: 0 };
      acc[key].value += row[valueKey] ?? 0;
      return acc;
    }, {})
  ).sort((a, b) => b.value - a.value);

/** Per-day rows → chronological series with a plain `date` key. */
const foldTrend = (rows, dateKey, valueKeys) =>
  Object.values(
    (rows || []).reduce((acc, row) => {
      const date = readDate(row[dateKey]);
      if (!acc[date]) {
        acc[date] = { date };
        valueKeys.forEach((k) => (acc[date][k] = 0));
      }
      valueKeys.forEach((k) => (acc[date][k] += row[k] ?? 0));
      return acc;
    }, {})
  ).sort((a, b) => String(a.date).localeCompare(String(b.date)));

const hasAny = (items) => items.some((i) => (i.value ?? i.users ?? 0) > 0);

/**
 * iOS against Android, on the metrics where a platform gap usually means a
 * bug rather than a preference.
 *
 * Shown side by side rather than behind the platform filter: a comparison
 * nobody has to opt into is a comparison people actually make, and mobile
 * defects hide precisely in the gap between two platforms.
 */
export const PlatformSplit = ({ rows }) => {
  const byPlatform = (rows || []).reduce((acc, row) => {
    const key = row.platform || "Unknown";
    if (!acc[key]) {
      acc[key] = { platform: key, active_users: 0, started_recording: 0, completed_upload: 0 };
    }
    acc[key].active_users += row.active_users ?? 0;
    acc[key].started_recording += row.started_recording ?? 0;
    acc[key].completed_upload += row.completed_upload ?? 0;
    return acc;
  }, {});

  const platforms = Object.values(byPlatform).sort((a, b) => b.active_users - a.active_users);

  // With one platform there is nothing to compare, and a lone column would
  // imply the other is at zero rather than absent.
  if (platforms.length < 2) return null;

  const label = (p) => (p.platform === "IOS" ? "iOS" : p.platform === "ANDROID" ? "Android" : p.platform);

  return (
    <div className="split">
      {platforms.map((p) => (
        <div className="split__col" key={p.platform}>
          <h4>{label(p)}</h4>
          <dl>
            <div>
              <dt>Active parents</dt>
              <dd>{formatNumber(p.active_users)}</dd>
            </div>
            <div>
              <dt>Started recording</dt>
              <dd>{formatNumber(p.started_recording)}</dd>
            </div>
            <div>
              <dt>Videos finished</dt>
              <dd>{formatNumber(p.completed_upload)}</dd>
            </div>
            <div>
              <dt>Finished per start</dt>
              <dd>{formatRate(p.completed_upload, p.started_recording)}</dd>
            </div>
          </dl>
        </div>
      ))}
    </div>
  );
};

/* ---------------------------------------------------------- 1 · acquire */

export const AcquisitionSection = ({ data, summary }) => {
  const steps = foldActivationSteps(data.activationFunnel);
  const trend = foldTrend(data.newUsersTrend, "acquisition_date", [
    "new_users",
    "activated_users",
  ]);
  // `auth_method` can only be known for parents who fired login or sign_up
  // during the collection window. Anyone already signed in when collection
  // began has none — that is missing attribution, not a fourth sign-in option,
  // so it is reported as coverage rather than charted as a method.
  const allMethods = foldBy(data.signupMethods, "auth_method", "new_users");
  const methods = allMethods.filter((m) => m.raw !== "unknown");
  const known = methods.reduce((sum, m) => sum + m.value, 0);
  const unknown = allMethods.find((m) => m.raw === "unknown")?.value ?? 0;

  const median = summary?.median_days_to_first_video;

  return (
    <section className="section section--acquire" id="acquisition" aria-labelledby="h-acquisition">
      <div className="section__inner">
      <SectionHead
        id="h-acquisition"
        question="Are we growing?"
        title="Acquisition & activation"
        description="New parents arriving, and whether they reach the point of making a video."
        pill={
          median !== null && median !== undefined ? (
            <Pill tone="good">Median {median} days to first video</Pill>
          ) : null
        }
      />

      <Card
        title="From sign-up to first video"
        description="Each step counts people, so the percentages never go above 100%."
        isEmpty={!hasAny(steps)}
        emptyMessage="No sign-ups recorded in this period."
      >
        <FunnelSteps steps={steps} />
      </Card>

      <Card
        title="New parents over time"
        description="Parents seen for the first time, and how many went on to make a video."
        isEmpty={trend.length === 0}
      >
        <AreaChart
          data={trend}
          xKey="date"
          label="New parents, and how many went on to make a video"
          series={[
            { key: "new_users", label: "New", tone: "accent" },
            { key: "activated_users", label: "Made a video", tone: "clay" },
          ]}
        />
      </Card>

      {/* Deliberately a plain list rather than a chart. Given equal visual
          weight to the funnel it reads as acquisition channel, which it is
          not — no install attribution exists, so it cannot say where anyone
          came from, only which button they pressed. */}
      {hasAny(methods) && (
        <div className="inline-stats">
          <h4>How they sign in</h4>
          <ul>
            {methods.map((m) => (
              <li key={m.label}>
                <span>{m.label}</span>
                <b>{formatNumber(m.value)}</b>
                <em>{known > 0 ? formatPercent(m.value / known, 0) : "—"}</em>
              </li>
            ))}
          </ul>
          <p>
            The button they pressed. It does not tell us where they heard about Neemo — nothing
            tracks that yet.
            {unknown > 0 && (
              <>
                {" "}
                Based on the {formatNumber(known)} parent{known === 1 ? "" : "s"} who signed in
                during this period. {formatNumber(unknown)} more were already signed in when we
                started collecting, so there is no way to tell how they joined.
              </>
            )}
          </p>
        </div>
      )}
      </div>
    </section>
  );
};

/* ----------------------------------------------------------- 2 · create */

export const CreationSection = ({ data, summary }) => {
  const steps = orderSteps(data.creationFunnel);
  const trend = foldTrend(data.funnelTrend, "event_date", [
    "started_recording",
    "completed_upload",
  ]);
  const failures = foldBy(data.uploadFailures, "error_stage", "affected_users");

  // A version's first appearance stands in for its ship date — close enough to
  // explain a step change, and labelled as an estimate rather than a fact.
  const releases = (data.releases || [])
    .map((r) => ({ date: readDate(r.first_seen_date), label: `v${r.app_version}` }))
    .filter((r, i, all) => all.findIndex((x) => x.label === r.label) === i);

  // Failures broken down by build, which is the cut that turns "saving fails"
  // into something an engineer can act on.
  const failuresByVersion = foldBy(data.failureDetail, "app_version", "affected_users");

  const started = steps[0]?.users ?? 0;
  const finished = steps[steps.length - 1]?.users ?? 0;
  const lost = started - finished;

  return (
    <section className="section section--create" id="creation" aria-labelledby="h-creation">
      <div className="section__inner">
      <SectionHead
        id="h-creation"
        question="Do they actually make a video?"
        title="Creation funnel"
        description="Where parents fall out between opening a template and having a finished video."
        pill={lost > 0 ? <Pill tone="crit">{formatNumber(lost)} videos lost</Pill> : null}
      />

      <Card
        title="Template open to finished video"
        description="How many parents reach each step. Someone who tries twice counts once."
        isEmpty={!hasAny(steps)}
        emptyMessage="No recordings started in this period."
      >
        <FunnelSteps steps={steps} />
      </Card>

      <PlatformSplit rows={data.kpiDaily} />

      <div className="grid-2">
        <Card
          title="Where saving fails"
          description="Which step broke, and how many parents it cost. Videos blocked for needing a subscription are left out — that is the paywall working, not a fault."
          isEmpty={!hasAny(failures)}
          emptyMessage="Nothing failed in this period."
        >
          <BarList items={failures} tone="crit" />
          {failuresByVersion.length > 1 && (
            <div className="drilldown">
              <h5>By app version</h5>
              <BarList items={failuresByVersion} tone="crit" />
            </div>
          )}
        </Card>

        <Card
          title="Volume over time"
          description="Recordings started against videos completed. A widening gap means reliability slipping."
          isEmpty={trend.length === 0}
        >
          <AreaChart
            data={trend}
            xKey="date"
            label="Recordings started against videos completed over time"
            markers={releases}
            series={[
              { key: "started_recording", label: "Started recording", tone: "accent" },
              { key: "completed_upload", label: "Completed", tone: "clay" },
            ]}
          />
        </Card>
      </div>
      </div>
    </section>
  );
};

/* ---------------------------------------------------------- 3 · content */

export const ContentSection = ({ data }) => {
  const leaderboard = [...(data.templateLeaderboard || [])].sort(
    (a, b) => (b.opened_by ?? 0) - (a.opened_by ?? 0)
  );
  const categories = (data.categories || [])
    .map((c) => ({ label: c.category || "unknown", value: c.template_openers ?? 0 }))
    .sort((a, b) => b.value - a.value);

  const thresholdNote =
    "No template has passed the minimum-opens threshold yet. That filter stops a template " +
    "opened once and completed once from showing 100% and topping the ranking.";

  return (
    <section className="section section--content" id="content" aria-labelledby="h-content">
      <div className="section__inner">
      <SectionHead
        id="h-content"
        question="What should we make more of?"
        title="Content performance"
        description="Templates are commissioned, not user-generated, so this is the most direct lever the team has."
        pill={
          leaderboard.length > 0 ? (
            <Pill tone="good">{leaderboard.length} templates ranked</Pill>
          ) : null
        }
      />

      <Card
        title="Template leaderboard"
        description="Conversion is the share of parents who opened a template and finished a video with it."
        isEmpty={leaderboard.length === 0}
        emptyMessage={thresholdNote}
      >
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Template</th>
                <th>Category</th>
                <th className="num">Opened</th>
                <th className="num">Previewed</th>
                <th className="num">Recorded</th>
                <th className="num">Completed</th>
                <th className="num">Conversion</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((row) => (
                <tr key={row.template}>
                  <td>{row.template}</td>
                  <td className="dim">{row.category}</td>
                  <td className="num">{formatNumber(row.opened_by)}</td>
                  <td className="num">{formatNumber(row.previewed_by)}</td>
                  <td className="num">{formatNumber(row.recorded_by)}</td>
                  <td className="num">{formatNumber(row.completed_by)}</td>
                  <td>
                    <div className="conv">
                      <div className="conv__track">
                        <div
                          className="conv__fill"
                          style={{
                            width: `${Math.min((row.open_to_video_rate ?? 0) * 100, 100)}%`,
                          }}
                        />
                      </div>
                      <span className="num">{formatPercent(row.open_to_video_rate)}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card
        title="Categories"
        description="Parents who opened a template in each."
        isEmpty={!hasAny(categories)}
      >
        <BarList items={categories} />
      </Card>
      </div>
    </section>
  );
};

/* --------------------------------------------------------- 4 · retention */

export const RetentionSection = ({ data }) => {
  // Retention rows are per signup-week cohort; average them per window so the
  // card reads as one curve rather than one bar per cohort.
  const kpis = Object.values(
    (data.retentionKpi || []).reduce((acc, row) => {
      const key = row.window_name;
      if (!acc[key]) acc[key] = { label: key, day_order: row.day_order, rates: [] };
      if (row.retention_rate !== null && row.retention_rate !== undefined) {
        acc[key].rates.push(row.retention_rate);
      }
      return acc;
    }, {})
  )
    .sort((a, b) => a.day_order - b.day_order)
    .map((row) => ({
      label: humanLabel(row.label),
      value: row.rates.length ? row.rates.reduce((a, b) => a + b, 0) / row.rates.length : 0,
    }));

  // The curve arrives already pooled and size-weighted, one row per offset —
  // averaging per-cohort rates here would let a 3-user cohort swing it as hard
  // as a 300-user one, which is how this was previously wrong.
  const curve = [...(data.retentionCurve || [])]
    .sort((a, b) => a.day_offset - b.day_offset)
    .map((row) => ({ date: `Day ${row.day_offset}`, retention_rate: row.retention_rate ?? 0 }));

  const cohortDepth = Math.max(...(data.retentionCurve || []).map((r) => r.cohorts ?? 0), 0);

  const northStar = foldTrend(data.northStar, "week_start", [
    "weekly_creating_users",
    "weekly_active_users",
  ]);

  const resurrection = [...(data.resurrection || [])]
    .sort((a, b) => a.bucket_order - b.bucket_order)
    .map((row) => ({ label: row.lapse_bucket, value: row.returned_users ?? 0 }));
  const resurrectedTotal = resurrection.reduce((sum, r) => sum + r.value, 0);

  return (
    <section className="section section--retain" id="retention" aria-labelledby="h-retention">
      <div className="section__inner">
      <SectionHead
        id="h-retention"
        question="Do they come back?"
        title="Retention"
        description="The evidence for adding reminder notifications, which the app doesn't have today."
        pill={<Pill tone="warn">Needs several weeks of data</Pill>}
      />

      <div className="grid-2">
        <Card
          title="Still active after…"
          description="Of the parents who signed up, how many were still using Neemo later on."
          isEmpty={kpis.length === 0}
          emptyMessage="Not enough time has passed yet. The first figure appears once a sign-up is a day old, the next after a week. A blank here means the clock is still running, not that nobody came back."
        >
          <BarList items={kpis} isPercent />
        </Card>

        <Card
          title="Retention curve"
          description={
            cohortDepth > 0
              ? `Where the drop is steepest is when a reminder would need to arrive. Based on ${cohortDepth} group${cohortDepth === 1 ? "" : "s"} of sign-ups, weighted by size.`
              : "Where the cliff falls tells you when a reminder should fire."
          }
          isEmpty={curve.length < 3}
          emptyMessage="Needs at least three days of history before a trend means anything."
        >
          <AreaChart
            data={curve}
            xKey="date"
            label="How many parents were still active, by days since they joined"
            series={[{ key: "retention_rate", label: "Still active", tone: "accent" }]}
          />
        </Card>
      </div>

      <div className="grid-2">
        <Card
          title="Weekly creating parents"
          description="The headline number: people who made at least one video that week."
          isEmpty={northStar.length === 0}
        >
          <AreaChart
            data={northStar}
            xKey="date"
            label="Weekly creating parents against weekly active parents"
            series={[
              { key: "weekly_creating_users", label: "Creating", tone: "accent" },
              { key: "weekly_active_users", label: "Active", tone: "clay" },
            ]}
          />
        </Card>

        <Card
          title="Winning parents back"
          description="Parents who went quiet, then returned on their own — grouped by how long they were gone. How long they stayed away tells you when a reminder would need to arrive."
          pill={
            resurrectedTotal > 0 ? (
              <Pill tone="good">{formatNumber(resurrectedTotal)} returned</Pill>
            ) : null
          }
          isEmpty={resurrection.length === 0}
          emptyMessage="Nobody has gone quiet for a week and come back yet. This needs more than a week of history before it can say anything."
        >
          <BarList items={resurrection} tone="clay" />
        </Card>
      </div>
      </div>
    </section>
  );
};
